import { WorkoutCell } from 'components/WorkoutCell/WorkoutCell';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import ClipLoader from 'react-spinners/ClipLoader';
import { colors } from 'styleguide/colors';
import { ScreenContainer } from 'styleguide/ScreenContainer';
import { MIN_WIDTH_FOR_SIDEBAR_NAVIGATION, themeState } from 'styleguide/theme';
import { FeedViewModel } from './FeedViewModel';
import { LoadingCell } from './components/LoadingCell';
import { MobileCTACell } from './components/MobileCTACell/MobileCTACell';
import useIsOnScreen from 'utils/useIsOnScreen';
import { DisplayXS } from 'styleguide/Texts';
import { Spacing } from 'styleguide/spacing';
import { SuggestedAthletes } from './components/SuggestedAthletes/SuggestedAthletes';
import { ProfileCard } from './components/ProfileCard/ProfileCard';
import { View } from 'styleguide/View';

const ScreenWrapper = styled.div`
  @media (min-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION}px) {
    width: 100vw;
    height: 100vh;
  }
  overflow-y: scroll;
`;

const Container = styled(View)`
  flex-direction: row;
  max-width: 1148px;
  width: 100%;
  justify-content: center;
  @media (max-width: 1024px) {
    width: 704px;
  }
  @media (max-width: 704px) {
    width: 95%;
  }
  gap: ${Spacing.lg}px;
  margin: 0px ${Spacing.md}px;
`;

const LeftContainer = styled.div`
  max-width: 684px;
  width: 100%;
`;

const RightContainer = styled(View)`
  align-self: flex-start;
  width: 100%;
  position: sticky;
  max-width: 330px;
  top: ${Spacing.lg}px;
  gap: ${Spacing.md}px;
  @media (max-width: 1024px) {
    display: none;
  }
`;

const PagingLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${Spacing.md}px;
`;

const vm = new FeedViewModel();

export const FeedScreen = () => {
  const bottomOfWorkoutsRef = useRef<HTMLDivElement>(null);
  const isBottomVisible = useIsOnScreen(bottomOfWorkoutsRef);

  useEffect(() => {
    if (isBottomVisible) {
      vm.fetchMore();
    }
  }, [isBottomVisible]);

  useEffect(() => {
    vm.onMounted();
  }, []);

  return <FeedScreenInner vm={vm} bottomOfWorkoutsRef={bottomOfWorkoutsRef} />;
};

interface FeedScreenInnerProps {
  vm: FeedViewModel;
  bottomOfWorkoutsRef: React.Ref<HTMLDivElement>;
}

const FeedScreenInner = observer(({ vm, bottomOfWorkoutsRef }: FeedScreenInnerProps) => {
  return (
    <ThemeProvider theme={themeState.current}>
      <ScreenWrapper onScroll={vm.handleScroll}>
        <ScreenContainer
          style={{ paddingTop: Spacing.lg, display: 'flex', justifyContent: 'center' }}
        >
          <Container>
            <LeftContainer>
              <DisplayXS style={{ marginBottom: Spacing.md }}>Home</DisplayXS>
              {vm.cells.map(c => {
                switch (c.type) {
                  case 'workout-cell':
                    return <WorkoutCell key={c.vm.data.key} vm={c.vm} />;
                  case 'loading-cell':
                    return <LoadingCell key={c.key} />;
                  case 'mobile-cta':
                    return <MobileCTACell key="mobile-cta" />;
                }
              })}
              {vm.isFetchingMore && (
                <PagingLoadingContainer>
                  <ClipLoader size={30} color={colors.primary500} loading={true} />
                </PagingLoadingContainer>
              )}
              <div
                ref={bottomOfWorkoutsRef}
                style={vm.isFetchingMore || vm.isInitialFetch ? { display: 'none' } : {}}
              />
            </LeftContainer>
            <RightContainer>
              <ProfileCard />
              {vm.showSuggestedUsers && <SuggestedAthletes />}
            </RightContainer>
          </Container>
        </ScreenContainer>
      </ScreenWrapper>
    </ThemeProvider>
  );
});
